import React, { useState } from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import { usePatchDraft } from '@/hooks/use-onboarding-interview';
import { TextWidgetData, WidgetProps } from './widget-types';
import { WidgetCard } from './widget-card';

/**
 * Uma resposta curta de uma linha — hoje, o nome do negócio.
 *
 * O campo já vem no cartão porque é onde ele está olhando: mandar escrever na
 * caixa de mensagem funciona, mas obriga a mudar de lugar no meio do roteiro.
 *
 * "Pular" é resposta legítima, e por isso grava nulo em vez de não gravar
 * nada: sem isso o passo continuaria pendente e a conversa reperguntaria.
 */
export function TextWidget({
  data,
  editable,
  onAdvance,
}: WidgetProps<TextWidgetData>) {
  const patch = usePatchDraft();
  const [value, setValue] = useState(data.value ?? '');
  const [done, setDone] = useState(false);

  const canEdit = editable && !done;

  const save = async (answer: string | null) => {
    if (done) return;
    setDone(true);
    await patch.mutateAsync({ payload: { [data.field]: answer } });
    onAdvance?.(answer ?? data.skipLabel ?? undefined);
  };

  return (
    <WidgetCard title={data.title} icon="storefront-outline">
      {data.hint ? (
        <ThemedText className="text-xs text-foreground/45">
          {data.hint}
        </ThemedText>
      ) : null}

      {canEdit ? (
        <>
          <Input
            value={value}
            onChangeText={setValue}
            placeholder={data.placeholder ?? ''}
            autoCapitalize="words"
          />

          <View className="gap-2">
            <PressableScale
              onPress={() => void save(value.trim())}
              disabled={patch.isPending || !value.trim()}
              className={`items-center rounded-full py-2.5 ${
                value.trim() ? 'bg-primary' : 'bg-neutral-200'
              }`}
            >
              <ThemedText className="text-sm font-semibold text-white">
                {patch.isPending ? 'Salvando...' : data.confirmLabel}
              </ThemedText>
            </PressableScale>
            {data.skipLabel ? (
              <PressableScale
                onPress={() => void save(null)}
                className="items-center py-1"
              >
                <ThemedText className="text-xs font-semibold text-foreground/45">
                  {data.skipLabel}
                </ThemedText>
              </PressableScale>
            ) : null}
          </View>
        </>
      ) : data.value ? (
        <ThemedText className="text-sm text-foreground/70">
          {data.value}
        </ThemedText>
      ) : null}
    </WidgetCard>
  );
}
